import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import '../contracts/SubaccountsFranchises_ProvidersResponse';
import '../contracts/SubaccountsFranchises_ProviderDetailResponse';

/**
 * Composable for managing payment providers (ASAAS, Stripe, MercadoPago, etc.)
 *
 * This provides provider-agnostic operations for:
 * - Listing available payment providers
 * - Getting provider capabilities
 * - Getting provider configuration requirements
 */
export function usePaymentProviders() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * List all available payment providers
   * @returns {Array} Array of provider objects with code, name, countries, capabilities
   */
  const listProviders = async () => {
    log('usePaymentProviders', 'listProviders', 'Fetching available providers');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/index.php/subaccounts/providers', {
        responseContract: 'SubaccountsFranchises_ProvidersResponse',
      });
      log('usePaymentProviders', 'listProviders', 'Response', response);

      if (response.success) {
        const rawProviders = response.dados || [];

        // Transform data to expected format
        return rawProviders.map((provider: any) => ({
          ...provider,
          // Convert country_codes string to countries array
          countries: provider.country_codes
            ? (Array.isArray(provider.country_codes)
                ? provider.country_codes
                : provider.country_codes.split(',').map((c: any) => c.trim()))
            : [],
          // Convert capabilities object to array of enabled capabilities
          capabilities: provider.capabilities
            ? (Array.isArray(provider.capabilities)
                ? provider.capabilities
                : Object.keys(provider.capabilities).filter(key => provider.capabilities[key]))
            : []
        }));
      } else {
        throw new Error(response.error || 'Erro ao listar provedores de pagamento');
      }
    } catch (err) {
      log('usePaymentProviders', 'listProviders', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get detailed information about a specific provider
   * @param {string} providerCode - Provider code (e.g., 'asaas', 'stripe', 'mercadopago')
   * @returns {Object} Provider details including capabilities and config schema
   */
  const getProvider = async (providerCode: any) => {
    log('usePaymentProviders', 'getProvider', 'Fetching provider details', providerCode);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/subaccounts/providers/${providerCode}`, {
        responseContract: 'SubaccountsFranchises_ProviderDetailResponse',
      });
      log('usePaymentProviders', 'getProvider', 'Response', response);

      if (response.success) {
        return response.dados as any;
      } else {
        throw new Error(response.error || 'Provedor de pagamento não encontrado');
      }
    } catch (err) {
      log('usePaymentProviders', 'getProvider', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get capabilities for a specific provider
   * @param {string} providerCode - Provider code
   * @returns {Array} Array of capability strings (e.g., ['payments', 'invoices', 'dunning'])
   */
  const getProviderCapabilities = async (providerCode: any) => {
    log('usePaymentProviders', 'getProviderCapabilities', 'Fetching capabilities', providerCode);

    try {
      const provider = await getProvider(providerCode);
      return provider?.capabilities || [];
    } catch (err) {
      log('usePaymentProviders', 'getProviderCapabilities', 'Error', err);
      throw err;
    }
  };

  /**
   * Check if a provider supports a specific capability
   * @param {string} providerCode - Provider code
   * @param {string} capability - Capability to check (e.g., 'invoices', 'dunning')
   * @returns {boolean} True if provider supports the capability
   */
  const hasCapability = async (providerCode: any, capability: any) => {
    try {
      const capabilities = await getProviderCapabilities(providerCode);
      return capabilities.includes(capability);
    } catch (err) {
      log('usePaymentProviders', 'hasCapability', 'Error checking capability', err);
      return false;
    }
  };

  /**
   * Get configuration requirements for a provider
   * @param {string} providerCode - Provider code
   * @returns {Object} Configuration schema with required fields
   */
  const getProviderConfigSchema = async (providerCode: any) => {
    log('usePaymentProviders', 'getProviderConfigSchema', 'Fetching config schema', providerCode);

    try {
      const provider = await getProvider(providerCode);
      return provider?.config_schema || {};
    } catch (err) {
      log('usePaymentProviders', 'getProviderConfigSchema', 'Error', err);
      throw err;
    }
  };

  /**
   * Get list of countries supported by a provider
   * @param {string} providerCode - Provider code
   * @returns {Array} Array of country codes (e.g., ['BR'], ['US', 'CA'])
   */
  const getProviderCountries = async (providerCode: any) => {
    log('usePaymentProviders', 'getProviderCountries', 'Fetching countries', providerCode);

    try {
      const provider = await getProvider(providerCode);
      return provider?.countries || [];
    } catch (err) {
      log('usePaymentProviders', 'getProviderCountries', 'Error', err);
      throw err;
    }
  };

  /**
   * Get providers available for a specific country
   * @param {string} countryCode - Country code (e.g., 'BR', 'US')
   * @returns {Array} Array of providers supporting that country
   */
  const getProvidersForCountry = async (countryCode: any) => {
    log('usePaymentProviders', 'getProvidersForCountry', 'Fetching providers for country', countryCode);

    try {
      const providers = await listProviders();
      return providers.filter((p: any) =>
        p.countries && p.countries.includes(countryCode)
      );
    } catch (err) {
      log('usePaymentProviders', 'getProvidersForCountry', 'Error', err);
      throw err;
    }
  };

  /**
   * Get human-readable provider name
   * @param {string} providerCode - Provider code
   * @returns {string} Human-readable name
   */
  const getProviderDisplayName = (providerCode: any) => {
    const displayNames: any = {
      'asaas': 'ASAAS',
      'stripe': 'Stripe',
      'mercadopago': 'MercadoPago'
    };
    return displayNames[providerCode] || providerCode;
  };

  /**
   * Get provider-specific payment method options
   * @param {string} providerCode - Provider code
   * @returns {Array} Array of payment method objects
   */
  const getPaymentMethods = (providerCode: any) => {
    const methodsByProvider: any = {
      'asaas': [
        { value: 'any', label: 'Todos', icon: 'pi-wallet' },
        { value: 'pix', label: 'PIX', icon: 'pi-qrcode' },
        { value: 'boleto', label: 'Boleto', icon: 'pi-file' },
        { value: 'credit_card', label: 'Cartão de Crédito', icon: 'pi-credit-card' },
        { value: 'bank_transfer', label: 'Transferência', icon: 'pi-building' }
      ],
      'stripe': [
        { value: 'any', label: 'Todos', icon: 'pi-wallet' },
        { value: 'credit_card', label: 'Cartão de Crédito', icon: 'pi-credit-card' },
        { value: 'bank_transfer', label: 'ACH Transfer', icon: 'pi-building' }
      ],
      'mercadopago': [
        { value: 'any', label: 'Todos', icon: 'pi-wallet' },
        { value: 'pix', label: 'PIX', icon: 'pi-qrcode' },
        { value: 'boleto', label: 'Boleto', icon: 'pi-file' },
        { value: 'credit_card', label: 'Cartão de Crédito', icon: 'pi-credit-card' }
      ]
    };
    return methodsByProvider[providerCode] || methodsByProvider['asaas'];
  };

  /**
   * Get status display configuration
   * @param {string} status - Normalized status string
   * @returns {Object} Status display config with label, severity, icon
   */
  const getStatusConfig = (status: any) => {
    const statusConfigs: any = {
      // Account statuses
      'pending': { label: 'Pendente', severity: 'warning', icon: 'pi-clock' },
      'active': { label: 'Ativo', severity: 'success', icon: 'pi-check-circle' },
      'suspended': { label: 'Suspenso', severity: 'danger', icon: 'pi-ban' },
      'rejected': { label: 'Rejeitado', severity: 'danger', icon: 'pi-times-circle' },

      // Payment statuses
      'confirmed': { label: 'Confirmado', severity: 'info', icon: 'pi-check' },
      'received': { label: 'Recebido', severity: 'success', icon: 'pi-check-circle' },
      'overdue': { label: 'Vencido', severity: 'danger', icon: 'pi-exclamation-triangle' },
      'refunded': { label: 'Estornado', severity: 'secondary', icon: 'pi-replay' },
      'cancelled': { label: 'Cancelado', severity: 'secondary', icon: 'pi-times' },

      // Invoice statuses
      'scheduled': { label: 'Agendada', severity: 'info', icon: 'pi-calendar' },
      'processing': { label: 'Processando', severity: 'warning', icon: 'pi-spin pi-spinner' },
      'authorized': { label: 'Autorizada', severity: 'success', icon: 'pi-check-circle' },
      'error': { label: 'Erro', severity: 'danger', icon: 'pi-times-circle' }
    };
    return statusConfigs[status] || { label: status, severity: 'secondary', icon: 'pi-question' };
  };

  return {
    // State
    isLoading,
    error,

    // Provider operations
    listProviders,
    getProvider,
    getProviderCapabilities,
    hasCapability,
    getProviderConfigSchema,
    getProviderCountries,
    getProvidersForCountry,

    // Utility functions
    getProviderDisplayName,
    getPaymentMethods,
    getStatusConfig
  };
}
